import { Button } from "@/components/button"
import { FadeIn } from "@/components/shared/fade-in"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { LabeledSelect } from "@/components/ui/labeled-select"
import {
  type AbsenceReviewUiStore,
  type ActiveSortOption,
  type PendingSortOption,
  useReviewActiveSort,
  useReviewPendingSort,
  useReviewQueueFilter,
  useReviewSearchQuery,
} from "@/lib/stores/absence-review-ui-store"

const pendingSortOptions = [
  { value: "submitted_desc", label: "Submitted (newest first)" },
  { value: "submitted_asc", label: "Submitted (oldest first)" },
  { value: "member", label: "Member (A-Z)" },
] as const

const activeSortOptions = [
  { value: "range_asc", label: "Range start (earliest first)" },
  { value: "range_desc", label: "Range start (latest first)" },
  { value: "member", label: "Member (A-Z)" },
] as const

export function ReviewFilterCard({ store }: { store: AbsenceReviewUiStore }) {
  const searchQuery = useReviewSearchQuery(store)
  const queueFilter = useReviewQueueFilter(store)
  const pendingSort = useReviewPendingSort(store)
  const activeSort = useReviewActiveSort(store)

  return (
    <FadeIn delay={0.03}>
      <Card>
        <CardHeader>
          <CardTitle>Filter review queue</CardTitle>
          <CardDescription>
            Search by member, member id, or reason, then sort each table.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Input
            value={searchQuery}
            onChange={(event) =>
              store.getState().setSearchQuery(event.target.value)
            }
            placeholder="Search submissions"
          />
          <div className="flex flex-wrap gap-2">
            {(
              [
                { key: "all", label: "All" },
                { key: "pending", label: "Pending" },
                { key: "approved", label: "Active" },
              ] as const
            ).map((option) => (
              <Button
                key={option.key}
                type="button"
                size="sm"
                variant={queueFilter === option.key ? "default" : "outline"}
                onClick={() => {
                  store.getState().setQueueFilter(option.key)
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <LabeledSelect
              value={pendingSort}
              onValueChange={(value) =>
                store.getState().setPendingSort(value as PendingSortOption)
              }
              options={pendingSortOptions}
              label="Pending sort"
            />
            <LabeledSelect
              value={activeSort}
              onValueChange={(value) =>
                store.getState().setActiveSort(value as ActiveSortOption)
              }
              options={activeSortOptions}
              label="Active sort"
            />
          </div>
        </CardContent>
      </Card>
    </FadeIn>
  )
}
